import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../lib/i18n';

/**
 * Looking at an attachment without leaving the conversation (Phase 91).
 *
 * The thread used to open every image in a new browser tab, on the reasoning that the browser is
 * already an image viewer. It is — and an agent mid-conversation is not browsing: they are checking
 * whether the photograph shows the broken part, and then answering. A tab switch loses the thread,
 * the draft and the place in it.
 *
 * So it opens here, over the conversation, and closes the way everything else with an overlay
 * closes: Escape, a click on the backdrop, or the button. The file is still one click from being
 * downloaded, because sometimes looking is not what somebody needs.
 *
 * Rendered through a portal so a bubble's `overflow` cannot clip it, which is the bug every inline
 * lightbox has.
 */

export interface ViewerItem {
  url: string;
  filename: string;
  mimeType: string;
}

function kindOf(mimeType: string): 'image' | 'video' | 'audio' | 'pdf' | 'file' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';

  return 'file';
}

export function MediaViewer({
  item,
  onClose,
}: {
  item: ViewerItem;
  onClose: () => void;
}): React.JSX.Element {
  const kind = kindOf(item.mimeType);

  const onKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', onKey);

    /*
     * The page behind does not scroll while this is open.
     *
     * Restored on the way out rather than set to a fixed value: another overlay may have had an
     * opinion about it first, and stamping `auto` over that is how two modals fight.
     */
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onKey]);

  return createPortal(
    <div
      className="viewer"
      role="dialog"
      aria-modal="true"
      aria-label={item.filename}
      onClick={onClose}
    >
      <header className="viewer__bar" onClick={(event) => event.stopPropagation()}>
        <span className="viewer__name" title={item.filename}>
          {item.filename}
        </span>

        <span className="viewer__actions">
          <a
            className="button button--quiet button--compact"
            href={item.url}
            download={item.filename}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('inbox.viewerDownload')}
          </a>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={onClose}
            aria-label={t('inbox.viewerClose')}
          >
            ✕
          </button>
        </span>
      </header>

      {/* The content stops the click, so only the backdrop closes. */}
      <div className="viewer__stage" onClick={(event) => event.stopPropagation()}>
        {kind === 'image' && <img className="viewer__image" src={item.url} alt={item.filename} />}

        {kind === 'video' && (
          <video className="viewer__video" src={item.url} controls autoPlay playsInline />
        )}

        {kind === 'audio' && <audio className="viewer__audio" src={item.url} controls autoPlay />}

        {/*
          A PDF in the browser's own reader: pagination, search and printing are already there, and
          rebuilding any of it would be worse than what every reader on the machine already does.
        */}
        {kind === 'pdf' && (
          <iframe className="viewer__frame" src={item.url} title={item.filename} />
        )}

        {kind === 'file' && (
          <p className="viewer__plain">
            {t('inbox.viewerNoPreview')}
            <a href={item.url} download={item.filename} target="_blank" rel="noopener noreferrer">
              {item.filename}
            </a>
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
